import { ComponentPropsWithoutRef, forwardRef } from "react";
import styles from "./Drawer.module.scss";
import cx from "classnames";

/**
 * Props for the DrawerFooter component
 * @extends ComponentPropsWithoutRef<"footer">
 */
export type DrawerFooterProps = ComponentPropsWithoutRef<"footer"> & {
  /**
   * When true, the footer will stick to the bottom of the drawer.
   * @default false
   */
  sticky?: boolean;
};

/**
 * Footer component for the drawer that displays action buttons and controls.
 *
 * Features:
 * - Flexible layout for action buttons and controls
 * - Optional sticky positioning at the bottom
 * - Proper semantic HTML structure
 * - Accessible with proper ARIA attributes
 * - Consistent styling with the design system
 * - Supports all standard footer element props
 * - Responsive design that adapts to content
 *
 * @example
 * <Drawer.Footer sticky>
 *   <Button onClick={handleSave}>Save</Button>
 *   <Button onClick={handleCancel}>Cancel</Button>
 * </Drawer.Footer>
 */
export const DrawerFooter = forwardRef<HTMLElement, DrawerFooterProps>(
  (props, ref) => {
    const { className, children, sticky = false, ...rest } = props;
    const drawerFooterClasses = cx(styles.footer, className, {
      [styles["sticky"]]: sticky,
    });

    return (
      <footer
        className={drawerFooterClasses}
        data-anv="drawer-footer"
        ref={ref}
        {...rest}
      >
        {children}
      </footer>
    );
  },
);

DrawerFooter.displayName = "DrawerFooter";
